import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { ButtonExportCSVAsync, DateRangePicker, FieldWrapper, Filter, FilterButton, Input, LedgerTable, PaginatedList, Section, Select } from 'components';
import { appConfig } from 'config/app';
import { DDOption } from 'config/const';
import { getQueryStringValue, useDebounce, useQueryString } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPrefundLedgerRequest, Ledger, selectLedgerEntries } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { ApiQueryOperator, IApiList, IApiQueryParam, ILedgerEntry } from 'types';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { formatDateForApi } from 'utils/date';
import { DEFAULT_OFFSET, DEFAULT_PAGE_SIZE } from 'utils/table';
import * as API from '../../api';
import { ReportTableHeader } from './components';

const ledgerFilterTypesOptions = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['credit'],
    },
    label: 'Credit',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['debit'],
    },
    label: 'Debit',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['pending_credit'],
    },

    label: 'Pending Credit',
  },
];

const DEFAULT_START_DATE = formatDateForApi(dayjs().subtract(appConfig.DATE_RANGE.DEFAULT_DAYS, 'days'));
const DEFAULT_END_DATE = formatDateForApi(dayjs());

const ReportsLedger = () => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const ledger = useSelector(selectLedgerEntries());
  const total = ledger?.total || 0;
  const ledgerEntries = ledger?.data || [];
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Ledger.GET_PREFUND_LEDGER_REQUEST));

  const [urlQuery, setUrlQuery] = useQueryString('reportsLedger', window.location, {
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_OFFSET,
    sort: 'created_at',
    q: [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: DEFAULT_START_DATE },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: DEFAULT_END_DATE },
    ],
  });
  const qWithoutDates = (urlQuery.q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');
  const startDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.gte) as string;
  const endDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.lte) as string;

  // Fetch ledger entries when query params change
  useEffect(() => {
    if (currentSite?.id && startDate && endDate) {
      dispatch(getPrefundLedgerRequest(currentSite.id, startDate, endDate, { ...urlQuery, q: qWithoutDates }));
    }
  }, [currentSite?.id, dispatch, urlQuery]);

  const onFilterChange = (key: string, value: string | string[] | DDOption[]) => {
    const otherFilters = (urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== key);
    let newFilterParams: IApiQueryParam[] = [];

    switch (key) {
      case 'payment_date': {
        const dateRange = value as string[];
        newFilterParams = [
          { key: 'payment_date', operator: ApiQueryOperator.gte, value: dateRange[0] },
          { key: 'payment_date', operator: ApiQueryOperator.lte, value: dateRange[1] },
        ];
        break;
      }
      case 'entry_type': {
        const newEntryType = value as DDOption[];
        if (newEntryType?.length && newEntryType[0]?.value) newFilterParams = [newEntryType[0].value];
        break;
      }
      default: {
        const stringValue = (value as string).trim();
        if (stringValue !== '') newFilterParams = [{ key, operator: ApiQueryOperator.like, value: value as string }];
        break;
      }
    }

    setUrlQuery({ q: [...otherFilters, ...newFilterParams], offset: 0 });
  };
  const debounceOnDescriptionChange = useDebounce((newValue: string) => onFilterChange('description', newValue), 500);

  const exportCSV = async (siteId: string, startDate: string, endDate: string, options: IApiQueryOptions): Promise<IExportCSV> => {
    const response: ApiResponse<IApiList<ILedgerEntry>> = await API.Sites.listPrefundLedgerEntries(siteId, startDate, endDate, options);
    return CSVFormatter.ledger(response.body);
  };

  return (
    <Section data-cy="table">
      <ReportTableHeader
        title="Results"
        count={total || 0}
        actions={
          <>
            <DateRangePicker
              fluidWidth={false}
              inputHeight="small"
              onSubmit={([start, end]: string[]) => onFilterChange('payment_date', [start, end])}
              pickerPosition="right"
              startDate={DEFAULT_START_DATE}
              endDate={DEFAULT_END_DATE}
              submitLabel="apply"
              maxDays={appConfig.DATE_RANGE.MAX_DAYS_3_MONTHS}
              setGlobalDateRange={false}
            />
            <FilterButton filterActive={filterActive} appliedFilters={qWithoutDates.length} onClick={() => setFilterActive(!filterActive)} />
            <ButtonExportCSVAsync
              disabled={!(total && total >= 1) || isLoading}
              exportFunc={() => {
                const options = { offset: 0, limit: 0, q: qWithoutDates };
                return exportCSV(currentSite.id, startDate as string, endDate as string, options);
              }}
              fileName={`Ledger_${currentSite.name}_${startDate}_${endDate}`}
            />
          </>
        }
        isLoading={isLoading}
      />
      <Filter isVisible={filterActive}>
        <FieldWrapper label="Type" data-cy="filter-type">
          <Select placeholder="All" multi={false} values={[]} options={ledgerFilterTypesOptions} onChange={(value: DDOption[]) => onFilterChange('entry_type', value)} data-cy="type-filter" />
        </FieldWrapper>
        <FieldWrapper label="Description" data-cy="filter-description">
          <Input placeholder="Description" onChange={(e: React.FormEvent<HTMLInputElement>) => debounceOnDescriptionChange(e.currentTarget.value)} />
        </FieldWrapper>
      </Filter>

      <PaginatedList total={total} limit={urlQuery.limit} offset={urlQuery.offset} onPageChange={(options: IApiQueryOptions) => setUrlQuery(options)} isLoading={isLoading} standalone={false}>
        <LedgerTable isLoading={isLoading} ledgerEntries={ledgerEntries} urlQuery={urlQuery} onSortChange={(newSort: string) => setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort, offset: 0 })} />
      </PaginatedList>
    </Section>
  );
};

export default ReportsLedger;
